import { MenuItem, Select, SelectProps, styled, Box } from '@mui/material';
import { RowBox } from '../../styles/commonStyles';
import { theme } from '../../configs/theme';
import { TextWrapper, TextWrapperProps } from './TextWrapper';
import ConditionalWrapper from '../wrappers/ConditionalWrapper';

/**
 * Props for the Dropdown component.
 */
type DropdownProps = {
  /**
   * Icon component to display alongside the dropdown.
   */
  icon?: React.ReactNode;

  /**
   * Array of data items to populate the dropdown options.
   */
  data?: { title: string; value: string }[];

  /**
   * Name of the dropdown field, useful for form handling.
   */
  name?: string;

  /**
   * Callback function triggered when an item is selected.
   * @param {any} value - The selected value.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (event: any) => void;

  /**
   * Custom styles for the root container of the dropdown.
   */
  sx?: object;

  /**
   * The current selected value. If omitted, we'll fallback to ''.
   */
  value?: string;

  /**
   * Is there an error in the input field?
   */
  error?: boolean;

  /**
   * Text to display below if error.
   */
  errorText?: string;

  /**
   * If true, displays a red asterisk (*) next to the label to indicate a required field.
   */
  required?: boolean;

  /**
   * Additional props for the text wrapper.
   */
  wrapperProps?: Omit<TextWrapperProps, 'children'>;

  /**
   * The default value to display in the dropdown.
   */
  defaultValue?: string;

  disabled?: boolean;
};

/**
 * A dropdown component with optional icon and customizable styles.
 *
 * @param {DropdownProps} props - The props for the Dropdown component.
 * @returns {React.ReactElement} The rendered Dropdown component.
 *
 * @example
 * <Dropdown
 *    title="Select an option"
 *    icon={<FormatListBulleted />}
 *    data={['Option 1', 'Option 2', 'Option 3']}
 *    onChange={(value) => console.log(value)}
 *    sx={{ margin: '10px' }}
 * />
 */
export const Dropdown = ({
  defaultValue,
  icon,
  data,
  onChange,
  sx,
  name,
  value,
  error,
  errorText,
  required,
  disabled = false,
  wrapperProps,
}: DropdownProps) => {
  return (
    <ConditionalWrapper
      condition={!!wrapperProps}
      wrapper={(children) => (
        <TextWrapper sx={{ width: '100%' }} {...wrapperProps} required={required}>
          {children}
        </TextWrapper>
      )}
    >
      <RowBox
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        <SelectContainer
          sx={{
            ...sx,
            border: error ? `1px solid ${theme.palette.status.rejected}` : '1px solid grey',
            borderRadius: '8px',
            backgroundColor: theme.palette.customColors.inputBackground,
          }}
        >
          {icon}
          <SelectStyled
            onChange={onChange}
            displayEmpty
            name={name}
            disabled={disabled}
            value={value ?? defaultValue ?? ''}
          >
            {data?.map((item, index) => (
              <MenuItem key={index} value={item.value}>
                {item.title}
              </MenuItem>
            ))}
          </SelectStyled>
        </SelectContainer>
        {error && (
          <p style={{ color: theme.palette.status.rejected, fontSize: '12px', margin: 0 }}>
            {errorText}
          </p>
        )}
      </RowBox>
    </ConditionalWrapper>
  );
};

const SelectContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.customColors.inputBackground,
  width: '100%',
  borderRadius: '8px',
  padding: '10px',
  display: 'flex',
  flexFlow: 'row',
  alignItems: 'center',
}));

const SelectStyled = styled(Select)(() => ({
  width: '100%',
  height: '30px',
  boxShadow: 'none',
  '.MuiOutlinedInput-input': {
    padding: '0px',
  },
  '.MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));
